import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Receipt, Plus, CheckCircle2, XCircle, Clock, Trash2, Download, Loader2, DollarSign, Paperclip, Sparkles, CreditCard, ShieldAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const METHOD_LABEL = { sham_cash: "شام كاش", bank_transfer: "حوالة بنكية", syriatel_cash: "سيريتل كاش", cash: "نقداً", other: "أخرى", online: "دفع إلكتروني" };
const STATUS = {
  pending: { label: "قيد المراجعة", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  initiating: { label: "إنشاء جلسة الدفع", cls: "bg-blue-100 text-blue-800", Icon: Clock },
  processing: { label: "جارٍ التحقق", cls: "bg-blue-100 text-blue-800", Icon: Clock },
  checkout_failed: { label: "تعذر بدء الدفع", cls: "bg-red-100 text-red-800", Icon: XCircle },
  failed: { label: "فشلت عملية الدفع", cls: "bg-red-100 text-red-800", Icon: XCircle },
  cancelled: { label: "ألغيت عملية الدفع", cls: "bg-slate-100 text-slate-700", Icon: XCircle },
  verified: { label: "معتمدة", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  rejected: { label: "مرفوضة", cls: "bg-red-100 text-red-800", Icon: XCircle },
};

export default function Payments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [gateway, setGateway] = useState({ enabled: false, provider: null, display_name: null });
  const [onlinePlanId, setOnlinePlanId] = useState("");
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: 0, currency: "USD", method: "sham_cash", reference: "", months: 1, plan_id: "", note: "", receipt_file_id: null, receipt_name: "" });
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [p, plns] = await Promise.all([api.get("/payments"), api.get("/plans")]);
      setItems(p.data); setPlans(plns.data);
      const payable = (plns.data || []).filter(plan => plan.active !== false && Number(plan.price_usd) > 0);
      setOnlinePlanId(current => payable.some(plan => plan.id === current) ? current : (payable[0]?.id || ""));
    } catch (e) { toast.error(formatErr(e)); }
    try {
      const { data } = await api.get("/payment-gateway/status");
      setGateway(data || { enabled: false, provider: null, display_name: null });
    } catch {
      // Older deployments and demo mode keep online payments hidden until the API is available.
      setGateway({ enabled: false, provider: null, display_name: null });
    }
  };
  useEffect(() => { load(); }, []);

  const onlinePlans = plans.filter(plan => plan.active !== false && Number(plan.price_usd) > 0);
  const canStartOnlineCheckout = ["super_admin", "company_manager", "accountant"].includes(user?.role);

  const startOnlineCheckout = async () => {
    if (!onlinePlanId) return toast.error("اختر خطة مدفوعة أولاً");
    setOnlineLoading(true);
    try {
      const idempotencyKey = window.crypto?.randomUUID?.()
        || `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const { data } = await api.post("/payments/checkout", { plan_id: onlinePlanId }, {
        headers: { "Idempotency-Key": idempotencyKey },
      });
      if (!data?.checkout_url) throw new Error("لم تُرجع بوابة الدفع رابطًا آمنًا");
      window.location.assign(data.checkout_url);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setOnlineLoading(false); }
  };

  const openNew = () => {
    setForm({ amount: 0, currency: "USD", method: "sham_cash", reference: "", months: 1, plan_id: plans[0]?.id || "", note: "", receipt_file_id: null, receipt_name: "" });
    setOpen(true);
  };

  const readB64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => { const s = String(r.result || ""); const i = s.indexOf(","); res(i >= 0 ? s.slice(i + 1) : s); };
    r.onerror = rej; r.readAsDataURL(file);
  });

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("الحد الأقصى 5MB");
    setUploading(true);
    try {
      const b64 = await readB64(f);
      const { data } = await api.post("/payments/upload-receipt", { name: f.name, content_type: f.type || "application/octet-stream", data_b64: b64 });
      setForm(x => ({ ...x, receipt_file_id: data.file_id, receipt_name: f.name }));
      toast.success("تم رفع الإيصال");
      // Auto-analyze if it's an image
      if (f.type.startsWith("image/")) {
        parseReceipt(data.file_id);
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const parseReceipt = async (fid) => {
    const file_id = fid || form.receipt_file_id;
    if (!file_id) return toast.error("ارفع صورة إيصال أولاً");
    setParsing(true);
    try {
      const { data } = await api.post("/payments/parse-receipt", { file_id });
      const p = data.parsed || {};
      setForm(x => ({
        ...x,
        amount: p.amount || x.amount,
        currency: p.currency || x.currency,
        method: p.method || x.method,
        reference: p.reference || x.reference,
        note: p.sender_name ? `المرسل: ${p.sender_name}${x.note ? " · " + x.note : ""}` : x.note,
      }));
      toast.success(`تم استخراج البيانات بدقة ${p.confidence || 0}%`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setParsing(false); }
  };

  const save = async () => {
    if (!form.amount || form.amount <= 0) return toast.error("المبلغ مطلوب");
    if (!form.reference && form.method !== "cash") return toast.error("رقم/مرجع التحويل مطلوب");
    setSaving(true);
    try {
      const body = {
        amount: form.amount, currency: form.currency, method: form.method,
        reference: form.reference, months: form.months, plan_id: form.plan_id || null,
        note: form.note, receipt_file_id: form.receipt_file_id,
      };
      await api.post("/payments", body);
      toast.success("تم تسجيل الدفعة — بانتظار اعتماد المدير العام");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const verify = async (id) => {
    if (!confirm("اعتماد الدفعة؟ سيتم تمديد الاشتراك تلقائياً.")) return;
    try { await api.post(`/payments/${id}/verify`); toast.success("تم الاعتماد وتمديد الاشتراك"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const reject = async (id) => {
    if (!confirm("رفض الدفعة؟")) return;
    try { await api.post(`/payments/${id}/reject`); toast.success("تم الرفض"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    if (!confirm("حذف؟")) return;
    try { await api.delete(`/payments/${id}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const totalVerifiedUSD = useMemo(() => items.filter(x => x.status === "verified" && x.currency === "USD").reduce((s, x) => s + (+x.amount || 0), 0), [items]);
  const totalPending = useMemo(() => items.filter(x => ["pending", "initiating", "processing"].includes(x.status)).length, [items]);

  return (
    <div className="space-y-4" data-testid="payments-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><Receipt className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">دفعات الاشتراك</h1>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? "اعتمد الدفعات المستلمة لتمديد اشتراكات شركات عملائك تلقائياً" : "سجّل دفعات اشتراكك ليتم اعتمادها من المدير العام"}
          </p>
        </div>
        {!isAdmin && <Button onClick={openNew} className="gap-2" data-testid="new-payment-btn"><Plus className="w-4 h-4" /> تسجيل دفعة</Button>}
      </div>

      {isAdmin && !gateway.enabled && (
        <Card className="p-4 border-amber-300 bg-amber-50/70 dark:border-amber-700 dark:bg-amber-950/20" data-testid="gateway-admin-status">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="font-bold text-amber-900 dark:text-amber-100">بوابة الدفع الإلكتروني غير مفعّلة</div>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">لم يُربط مزوّد حتى الآن. تبقى الحوالات والدفعات اليدوية متاحة، ولن يُفعّل أي اشتراك إلكترونيًا قبل وصول إشعار دفع موقّع من المزوّد.</p>
            </div>
          </div>
        </Card>
      )}

      {!isAdmin && canStartOnlineCheckout && gateway.enabled && onlinePlans.length > 0 && (
        <Card className="p-4 border-primary/40 bg-primary/5" data-testid="online-checkout-card">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <Label>الدفع الإلكتروني الآمن · {gateway.display_name}</Label>
              <Select value={onlinePlanId} onValueChange={setOnlinePlanId}>
                <SelectTrigger className="mt-1" data-testid="online-plan-select"><SelectValue placeholder="اختر الخطة" /></SelectTrigger>
                <SelectContent>{onlinePlans.map(plan => <SelectItem key={plan.id} value={plan.id}>{plan.name} — ${Number(plan.price_usd).toFixed(2)} / {plan.months} شهر</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={startOnlineCheckout} disabled={onlineLoading || !onlinePlanId} className="gap-2" data-testid="start-online-checkout">
              {onlineLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              المتابعة إلى الدفع
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="p-4 border-emerald-200 bg-emerald-50">
          <div className="text-xs text-emerald-700">إجمالي المعتمد (USD)</div>
          <div className="text-2xl font-bold text-emerald-900">${totalVerifiedUSD.toFixed(2)}</div>
        </Card>
        <Card className="p-4 border-amber-200 bg-amber-50">
          <div className="text-xs text-amber-700">بانتظار الاعتماد</div>
          <div className="text-2xl font-bold text-amber-900">{totalPending}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">إجمالي الدفعات</div>
          <div className="text-2xl font-bold">{items.length}</div>
        </Card>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {isAdmin && <TableHead>الشركة</TableHead>}
              <TableHead>التاريخ</TableHead>
              <TableHead>المبلغ</TableHead>
              <TableHead>الطريقة</TableHead>
              <TableHead>المرجع</TableHead>
              <TableHead>الأشهر</TableHead>
              <TableHead>الإيصال</TableHead>
              <TableHead>الحالة</TableHead>
              <TableHead className="text-center">إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(p => {
              const St = STATUS[p.status]?.Icon || Clock;
              return (
                <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                  {isAdmin && <TableCell className="font-bold">{p.company_name}</TableCell>}
                  <TableCell className="text-xs font-mono" dir="ltr">{new Date(p.created_at).toLocaleString("ar-EG", { dateStyle: "short", timeStyle: "short" })}</TableCell>
                  <TableCell className="font-bold">{p.amount.toLocaleString("ar-EG")} {p.currency}</TableCell>
                  <TableCell className="text-sm">{METHOD_LABEL[p.method] || p.method}</TableCell>
                  <TableCell className="text-xs font-mono" dir="ltr">{p.reference || "—"}</TableCell>
                  <TableCell>{p.months}</TableCell>
                  <TableCell>
                    {p.receipt_file_id ? (
                      <a href={`${API}/support/file/${p.receipt_file_id}`} target="_blank" rel="noreferrer" className="text-primary text-xs flex items-center gap-1" data-testid={`receipt-${p.id}`}><Download className="w-3 h-3" /> إيصال</a>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-bold inline-flex items-center gap-1 ${STATUS[p.status]?.cls}`}>
                      <St className="w-3 h-3" /> {STATUS[p.status]?.label || p.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-center">
                      {isAdmin && p.method === "online" && ["initiating", "pending", "processing"].includes(p.status) && <span className="text-[10px] text-muted-foreground">بانتظار تأكيد البوابة</span>}
                      {isAdmin && p.status === "pending" && p.method !== "online" && (
                        <>
                          <Button size="sm" variant="outline" onClick={()=>verify(p.id)} className="h-7 text-xs text-emerald-700 border-emerald-400" data-testid={`verify-${p.id}`}><CheckCircle2 className="w-3 h-3" /></Button>
                          <Button size="sm" variant="outline" onClick={()=>reject(p.id)} className="h-7 text-xs text-destructive" data-testid={`reject-${p.id}`}><XCircle className="w-3 h-3" /></Button>
                        </>
                      )}
                      {isAdmin && p.method !== "online" && <Button size="sm" variant="ghost" onClick={()=>del(p.id)} className="h-7 text-xs text-destructive" data-testid={`del-payment-${p.id}`}><Trash2 className="w-3 h-3" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-6 text-muted-foreground">لا توجد دفعات بعد</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>تسجيل دفعة اشتراك</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {plans.length > 0 && (
              <div>
                <Label>الخطة (اختياري)</Label>
                <Select value={form.plan_id} onValueChange={(v)=>setForm({...form, plan_id: v, amount: plans.find(p=>p.id===v)?.price_usd || form.amount, months: plans.find(p=>p.id===v)?.months || form.months})}>
                  <SelectTrigger data-testid="pay-plan-select"><SelectValue placeholder="لا شيء" /></SelectTrigger>
                  <SelectContent>{plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — ${p.price_usd}/{p.months}شهر</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>المبلغ</Label><Input type="number" step="0.01" value={form.amount} onChange={(e)=>setForm({...form, amount:+e.target.value})} data-testid="pay-amount-input" /></div>
              <div>
                <Label>العملة</Label>
                <Select value={form.currency} onValueChange={(v)=>setForm({...form, currency:v})}>
                  <SelectTrigger data-testid="pay-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["USD","SYP","TRY","EUR"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>طريقة الدفع</Label>
                <Select value={form.method} onValueChange={(v)=>setForm({...form, method:v})}>
                  <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(METHOD_LABEL).map(([k,l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>عدد الأشهر</Label><Input type="number" min="1" value={form.months} onChange={(e)=>setForm({...form, months:+e.target.value || 1})} data-testid="pay-months-input" /></div>
            </div>
            <div><Label>رقم/مرجع التحويل</Label><Input value={form.reference} onChange={(e)=>setForm({...form, reference:e.target.value})} dir="ltr" className="font-mono" data-testid="pay-ref-input" /></div>
            <div>
              <Label>صورة الإيصال (اختياري، ≤5MB)</Label>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <input type="file" ref={fileRef} onChange={upload} className="hidden" data-testid="pay-file-input" accept="image/*,application/pdf" />
                <Button type="button" variant="outline" onClick={()=>fileRef.current?.click()} disabled={uploading} className="gap-1" data-testid="pay-upload-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {form.receipt_name || "رفع صورة"}
                </Button>
                {form.receipt_file_id && (
                  <Button type="button" variant="outline" onClick={()=>parseReceipt()} disabled={parsing} className="gap-1 text-primary border-primary/40" data-testid="pay-ai-parse-btn">
                    {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    تحليل بالذكاء الاصطناعي
                  </Button>
                )}
                {form.receipt_file_id && <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> تم الرفع</span>}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">💡 الذكاء الاصطناعي يقرأ الصورة ويملأ الحقول تلقائياً — تراجع النتيجة قبل الحفظ</div>
            </div>
            <div><Label>ملاحظة (اختياري)</Label><Input value={form.note} onChange={(e)=>setForm({...form, note:e.target.value})} data-testid="pay-note-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>إلغاء</Button>
            <Button onClick={save} disabled={saving} data-testid="save-payment-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "تسجيل"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
